import React, { useState, useEffect } from 'react';
import { ScrollText, Upload, Download, RefreshCw, Loader2, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogsTab = ({ token, toast }) => {
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsFilter, setLogsFilter] = useState('all');
  const [loadingLogs, setLoadingLogs] = useState(true);

  const loadActivityLogs = async (p = 1, typeFilter = 'all') => {
    setLoadingLogs(true);
    try {
      let url = `${API}/admin/activity-logs?page=${p}&limit=50`;
      if (typeFilter !== 'all') url += `&log_type=${typeFilter}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setActivityLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
      setLogsPage(p);
    } catch (e) {
      console.error('Error loading logs:', e);
    } finally {
      setLoadingLogs(false);
    }
  };

  const exportLogsCSV = async () => {
    try {
      let url = `${API}/admin/activity-logs/export`;
      if (logsFilter !== 'all') url += `?log_type=${logsFilter}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `sendbox_logs_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast({ title: 'Export réussi', description: 'Le fichier CSV a été téléchargé' });
    } catch (e) {
      toast({ title: 'Erreur', description: "Impossible d'exporter les logs", variant: 'destructive' });
    }
  };

  useEffect(() => { loadActivityLogs(1, 'all'); }, []);

  return (
    <div className="space-y-4" data-testid="logs-tab-content">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-amber-400" />
          Journal d'activité ({logsTotal} entrées)
        </h2>
        <div className="flex items-center gap-2">
          <select value={logsFilter} onChange={e => { setLogsFilter(e.target.value); loadActivityLogs(1, e.target.value); }} className="bg-slate-800 border border-blue-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500/40" data-testid="logs-type-filter">
            <option value="all">Tous</option>
            <option value="upload">Uploads</option>
            <option value="download">Downloads</option>
          </select>
          <Button onClick={() => loadActivityLogs(logsPage, logsFilter)} variant="outline" size="sm" className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid="logs-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={exportLogsCSV} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="logs-export-csv-btn"><Download className="w-4 h-4 mr-1.5" />Exporter CSV</Button>
        </div>
      </div>

      {loadingLogs ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
      ) : activityLogs.length === 0 ? (
        <div className="bg-slate-800/50 border border-blue-500/10 rounded-xl p-8 text-center">
          <ScrollText className="w-10 h-10 text-blue-300/30 mx-auto mb-3" />
          <p className="text-blue-300/50">Aucune activité enregistrée</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="logs-table">
              <thead>
                <tr className="border-b border-blue-500/20 text-blue-300/70">
                  <th className="px-4 py-3 text-left font-medium">Type</th>
                  <th className="px-4 py-3 text-left font-medium">Adresse IP</th>
                  <th className="px-4 py-3 text-left font-medium">Fichier</th>
                  <th className="px-4 py-3 text-left font-medium">Partage</th>
                  <th className="px-4 py-3 text-left font-medium">Utilisateur</th>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {activityLogs.map((log, i) => (
                  <tr key={i} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3">
                      {log.type === 'upload' ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs font-medium"><Upload className="w-3 h-3" /> Upload</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 text-xs font-medium"><Download className="w-3 h-3" /> Download</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-amber-300 font-mono text-xs bg-amber-500/10 px-2 py-1 rounded"><Globe className="w-3 h-3" />{log.ip || '-'}</span></td>
                    <td className="px-4 py-3 text-white max-w-[200px] truncate" title={log.filename}>{log.filename || '-'}</td>
                    <td className="px-4 py-3 text-blue-300 font-mono text-xs">{log.share_id || '-'}</td>
                    <td className="px-4 py-3 text-blue-300/70 text-xs">{log.user_email || '-'}</td>
                    <td className="px-4 py-3 text-blue-300/50 text-xs whitespace-nowrap">
                      {log.created_at ? new Date(typeof log.created_at === 'string' && !log.created_at.endsWith('Z') ? log.created_at + 'Z' : log.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {logsTotal > 50 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-blue-500/20">
              <p className="text-xs text-blue-300/50">Page {logsPage} / {Math.ceil(logsTotal / 50)}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={logsPage <= 1} onClick={() => loadActivityLogs(logsPage - 1, logsFilter)} className="border-blue-500/20 text-blue-300 text-xs" data-testid="logs-prev-btn">Précédent</Button>
                <Button size="sm" variant="outline" disabled={logsPage >= Math.ceil(logsTotal / 50)} onClick={() => loadActivityLogs(logsPage + 1, logsFilter)} className="border-blue-500/20 text-blue-300 text-xs" data-testid="logs-next-btn">Suivant</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminLogsTab;
